import { Component, ReactNode, TransitionEvent, TransitionEventHandler, createRef } from 'react'
import * as styles from 'components/features/pfm/InternalLayout/InternalLayout.styl'
import * as cn from 'classnames'
import { Subject, fromEventPattern, merge, of, fromEvent, combineLatest } from 'rxjs'
import * as debounce from 'lodash/debounce'
import withReadyEvent from 'hocs/withReadyEvent/withReadyEvent'
import { TooltipZoneNodeContext } from 'components/features/pfm/Tooltip/Tooltip'
import { Navigation } from 'components/features/pfm/Navigation/Navigation'
import { distinctUntilChanged, filter, mapTo, map, debounceTime, switchMap, takeUntil, scan, tap } from 'rxjs/operators'

export type InternalLayoutStateProps = {
  isMenuShown: boolean
  isNotificationsExpanded: boolean
  isMobileOrTablet: boolean
}

export type InternalLayoutDispatchProps = {
}

export type InternalLayoutOwnProps = {
  sidebar: ReactNode
  notifications?: ReactNode
  stickyHeader?: ReactNode
  shouldShowStickyHeader?: () => boolean
  preventHiddenHeader?: boolean
  isFooterHidden?: boolean
}

type InternalLayoutProps =
  & InternalLayoutStateProps
  & InternalLayoutDispatchProps
  & InternalLayoutOwnProps

type InternalLayoutState = {
  isMobileSidebarShown: boolean
  isStickyHeaderShown: boolean
  // prevent overflowing when user is at top of page
  stickyHeaderVisibility: 'hidden' | 'visible'

  // we disable animation when menu is shown,
  // and when it turns false -> true, we should enable it with little delay
  isMobileAnimationDisabled: boolean
}

const DEFAULT_SHOULD_SHOW_STICKY_HEADER = () => true
class InternalLayout extends Component<InternalLayoutProps, InternalLayoutState> {
  static defaultProps: Partial<InternalLayoutProps> = {
    shouldShowStickyHeader: DEFAULT_SHOULD_SHOW_STICKY_HEADER,
  }

  static getDerivedStateFromProps (
    nextProps: InternalLayoutProps,
    prevState: InternalLayoutState,
  ): Partial<InternalLayoutState> | null {
    if (nextProps.isMenuShown && !prevState.isMobileAnimationDisabled) {
      // here we disable with no delay
      return {
        isMobileAnimationDisabled: true,
      }
    }

    return null
  }

  private TOP_PAGE_MAIN_HEADER_OFFSET = 64
  private HEADER_SCROLL_DISTANCE = 16

  private mainHeaderRef = createRef<HTMLDivElement>()
  private contentRef = createRef<HTMLDivElement>()

  private destroySubject = new Subject()

  private isStickyHeaderGiven$ = new Subject<boolean>()

  private resizeDebounced$ = fromEvent(window, 'resize').pipe(debounceTime(30))

  // slice to remove quotes
  private mediaQueryStickyDisabled = window.matchMedia(styles.mediaStickyDisabled.slice(1, -1))
  private isStickyHeaderDisabled$ = merge(
    of(this.mediaQueryStickyDisabled.matches),
    fromEventPattern<boolean>(
      (handler) => {
        const handlerShim = (mediaQuery: MediaQueryListEvent) => handler(mediaQuery.matches)
        this.mediaQueryStickyDisabled.addListener(handlerShim)

        return {
          mediaQueryStickyDisabled: this.mediaQueryStickyDisabled,
          handlerShim,
        }
      },
      (_handler, context) => context.mediaQueryStickyDisabled.removeListener(context.handlerShim),
    ),
  )

  constructor (props: InternalLayoutProps, context?: any) {
    super(props, context)
    this.state = {
      isMobileSidebarShown: true,
      isStickyHeaderShown: false,
      stickyHeaderVisibility: 'hidden',
      isMobileAnimationDisabled: props.isMenuShown,
    }
  }

  render () {
    const {
      sidebar,
      notifications,
      stickyHeader,
      isNotificationsExpanded,
      isMenuShown,
      isMobileOrTablet,
      isFooterHidden,

      children,
    } = this.props

    const {
      isMobileSidebarShown,
      isStickyHeaderShown,
      stickyHeaderVisibility,
      isMobileAnimationDisabled,
    } = this.state

    const containerClassname = cn(styles.container, isMobileAnimationDisabled && styles.animationDisabled)
    const headerClassname = cn(styles.header, {
      [styles.isMobileSidebarHidden]: !isMobileSidebarShown && !isMenuShown,
    })
    const footerClassname = cn(styles.footer, {
      [styles.isMobileSidebarHidden]: !isMobileSidebarShown,
    })
    const notificationsClassname = cn(styles.notifications, notifications && isNotificationsExpanded && styles.isNotificationsExpanded)

    return (
      <div className={ containerClassname }>
        <header className={ headerClassname }>
          <div className={ styles.sidebar } ref={ this.mainHeaderRef }>
            { sidebar }
          </div>

          { notifications && <div className={ notificationsClassname }>{ notifications }</div> }

          { stickyHeader && (
            <div
              className={ cn(styles.stickyHeader, isStickyHeaderShown && styles.isShown) }
              onTransitionEnd={ this.handleSecondHeaderTransitionEnd }
              style={{
                visibility: stickyHeaderVisibility,
              }}
            >
              { stickyHeader }
            </div>
          )}
        </header>
        <section className={ styles.content } ref={ this.contentRef }>
          <TooltipZoneNodeContext.Provider value={ this.contentRef.current || document.body }>
            { children }
          </TooltipZoneNodeContext.Provider>
        </section>
        { isMobileOrTablet && !isFooterHidden && (
          <footer className={ footerClassname }>
            <Navigation mode='compact' />
          </footer>
        )}
      </div>
    )
  }

  componentDidMount () {
    this.initStickyHeader()
    this.initMobileSidebar()

    this.isStickyHeaderGiven$.next(!!this.props.stickyHeader)
  }

  componentDidUpdate (prevProps: InternalLayoutProps) {
    if (!this.props.isMenuShown && prevProps.isMenuShown) {
      // to avoid unwanted transition, disable it with delay
      this.enableMobileAnimation()
    }

    if (!this.props.stickyHeader !== !prevProps.stickyHeader) {
      this.isStickyHeaderGiven$.next(!!this.props.stickyHeader)
    }
  }

  componentWillUnmount () {
    this.destroySubject.next()
    this.enableMobileAnimation.cancel()
  }

  private scrollPosition$ = merge(
    fromEvent(window, 'scroll'),
    this.resizeDebounced$,
  )
    .pipe(
      map(() => window.scrollY)
    )

  private enableMobileAnimation = debounce(() => {
    this.setState({
      isMobileAnimationDisabled: false,
    })
  }, 50)

  private getScrollPosition$ () {
    return merge(
      of(window.scrollY),
      this.scrollPosition$,
    )
  }

  private isTargetInput = (event: Event) => {
    if (event && event.target) {
      const target = event.target as HTMLElement & { matches: (s: string) => boolean }
      return target.matches('input[type="text"],input[type="password"],textarea')
    }

    return true
  }

  private getShowByFocus$ () {
    if (!this.props.isMobileOrTablet) {
      return of(true)
    }

    return merge(
      of(true),
      fromEvent(document, 'focusin').pipe(
        filter(this.isTargetInput),
        mapTo(false),
      ),
      fromEvent(document, 'focusout').pipe(
        filter(this.isTargetInput),
        mapTo(true),
      ),
    ).pipe(
      distinctUntilChanged(),
    )
  }

  private initStickyHeader () {
    combineLatest([
      this.isStickyHeaderDisabled$,
      this.isStickyHeaderGiven$,
    ])
      .pipe(
        map(([isStickyHeaderDisabled, isStickyHeaderGiven]) => !isStickyHeaderDisabled && isStickyHeaderGiven),
        distinctUntilChanged(),
        switchMap((hasStickyHeader) => {
          if (hasStickyHeader) {
            const scroll$ = this.getScrollPosition$()
              .pipe(
                debounceTime(50),
                map(() => {
                  const shouldShowStickyHeader = this.props.shouldShowStickyHeader || DEFAULT_SHOULD_SHOW_STICKY_HEADER
                  return shouldShowStickyHeader()
                }),
                distinctUntilChanged(),
              )

            const focus$ = this.getShowByFocus$()

            return combineLatest(
              scroll$, focus$
            ).pipe(
              tap(([shownByScroll, shownByFocus]) => {
                let nextShown = shownByFocus
                if (shownByFocus) {
                  nextShown = shownByScroll
                }

                this.setState({
                  isStickyHeaderShown: nextShown,
                  stickyHeaderVisibility: nextShown ? 'visible' : this.state.stickyHeaderVisibility,
                })
              }),
            )
          }

          return of<any>(false)
            .pipe(
              tap(() => this.setState({
                isStickyHeaderShown: false,
                stickyHeaderVisibility: 'hidden',
              }))
            )
        }),
        takeUntil(this.destroySubject),
      )
      .subscribe()
  }

  private getIsScrollingUp$ () {
    return this.getScrollPosition$()
      .pipe(
        scan<number, { next: number, prev: number }>((acc, y) => ({
          next: y,
          prev: acc.next,
        }), { next: 0, prev: 0 }),
        map((history) => history.next <= history.prev),
        scan<boolean, { turningPoint: number, isScrollingDown: boolean }>((acc, isScrollingDown) => {
          return acc.isScrollingDown === isScrollingDown
            ? acc
            // if we change direction, reset turning point
            : { turningPoint: window.scrollY, isScrollingDown }
        }, { turningPoint: 0, isScrollingDown: false }),
        // wait enough scroll distance to change direction
        scan<{ turningPoint: number, isScrollingDown: boolean }, boolean>((isPrevScrollingDown, history) => {
          const delta = Math.abs(window.scrollY - history.turningPoint)
          if (delta > this.HEADER_SCROLL_DISTANCE) {
            // direction is changed
            return history.isScrollingDown
          }

          return isPrevScrollingDown
        }, true),
        distinctUntilChanged(),
      )
  }

  private getIsAtTopOfPage$ () {
    return this.getScrollPosition$()
      .pipe(
        map((y) => y <= this.TOP_PAGE_MAIN_HEADER_OFFSET),
        distinctUntilChanged(),
      )
  }

  private initMobileSidebar () {

    this.isStickyHeaderDisabled$
      .pipe(
        switchMap((isStickyHeaderDisabled) => {
          if (isStickyHeaderDisabled) {
            return of<any>(false)
              .pipe(
                tap(() => this.setState({ isMobileSidebarShown: true })),
              )
          }

          return combineLatest<any>(
            this.getIsScrollingUp$(),
            this.getIsAtTopOfPage$(),
            this.getShowByFocus$(),
          )
            .pipe(
              tap(([isScrollingUp, isAtTopOfPage, showByFocus]) => {
                let isMobileSidebarShown = showByFocus || Boolean(this.props.preventHiddenHeader)
                if (showByFocus) {
                  isMobileSidebarShown = isScrollingUp || isAtTopOfPage
                }
                if (this.state.isMobileSidebarShown !== isMobileSidebarShown) {
                  this.setState({ isMobileSidebarShown })
                }
              })
            )
        }),
        takeUntil(this.destroySubject),
      )
      .subscribe()
  }

  private handleSecondHeaderTransitionEnd: TransitionEventHandler<HTMLDivElement> = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName === 'transform' && !this.state.isStickyHeaderShown) {
      // hide second header after transition to prevent overflowing
      this.setState({
        stickyHeaderVisibility: 'hidden',
      })
    }
  }
}

export default withReadyEvent<InternalLayoutProps>(InternalLayout)
